import React, { CSSProperties, ReactElement } from 'react'
import { makeStyles } from '@material-ui/core'

import { useAccidentAtlasTileGridStyles } from '../lib/tileGridStyles'

const SKELETON_TILE_COUNT = 4
const DESKTOP_COLUMNS = 2

const useTileSkeletonStyles = makeStyles((theme) => ({
  root: {
    height: 200,
    width: '100%',
    backgroundColor: theme.palette.background.default,
    borderRadius: 12,
    boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.12)',
    padding: theme.spacing(6),
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  line: {
    height: 18,
    borderRadius: 4,
    backgroundColor: theme.palette.background.grey,
    animation: '$pulse 1.5s ease-in-out infinite',
  },
  lineHeadline: {
    width: '45%',
    marginBottom: theme.spacing(1),
  },
  lineBody: {
    width: '100%',
  },
  lineBodyShort: {
    width: '72%',
  },
  '@keyframes pulse': {
    '0%, 100%': {
      opacity: 1,
    },
    '50%': {
      opacity: 0.45,
    },
  },
}))

function TileSkeleton(): ReactElement {
  const classes = useTileSkeletonStyles()

  return (
    <div className={classes.root} aria-hidden>
      <div className={`${classes.line} ${classes.lineHeadline}`} />
      <div className={`${classes.line} ${classes.lineBody}`} />
      <div className={`${classes.line} ${classes.lineBodyShort}`} />
    </div>
  )
}

type TileSkeletonGridProps = {
  isDesktop: boolean
  ariaLabel: string
}

export default function TileSkeletonGrid({
  isDesktop,
  ariaLabel,
}: TileSkeletonGridProps): ReactElement {
  const classes = useAccidentAtlasTileGridStyles()
  const rowCount = Math.ceil(SKELETON_TILE_COUNT / DESKTOP_COLUMNS)

  if (isDesktop) {
    return (
      <div
        className={classes.tilesRows}
        aria-busy="true"
        aria-label={ariaLabel}
      >
        {Array.from({ length: rowCount }, (_, rowIndex) => (
          <div
            key={`skeleton-row-${rowIndex}`}
            className={classes.tilesRow}
            style={
              {
                '--tile-columns': DESKTOP_COLUMNS,
              } as CSSProperties
            }
          >
            {Array.from({ length: DESKTOP_COLUMNS }, (_, columnIndex) => {
              const index = rowIndex * DESKTOP_COLUMNS + columnIndex
              if (index >= SKELETON_TILE_COUNT) return null

              return <TileSkeleton key={`skeleton-tile-${index}`} />
            })}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className={classes.tilesGrid} aria-busy="true" aria-label={ariaLabel}>
      {Array.from({ length: SKELETON_TILE_COUNT }, (_, index) => (
        <TileSkeleton key={`skeleton-tile-${index}`} />
      ))}
    </div>
  )
}
